/**
 * prome-core 运行时注入层。
 *
 * prome-core 以 git submodule 形式挂到各子应用的 `src/core`，源码级共享，
 * 由各应用自己的构建链编译。因此包内**不允许**出现：
 *   - `@/xxx` 应用别名（各应用别名不一致，recommend 没有 `@`）
 *   - `import { history } from 'umi'`（umi3/umi4 的 history 版本与 listen 签名不同）
 *   - React 18 独有 API（recommend 仍是 React 16）
 *   - tailwind class（recommend 没有 tailwind 构建链）
 *
 * 所有应用侧差异统一从这里注入：各应用启动时调用一次 `setAiRuntime()`。
 */
import type { EntityAction, ModuleSuggestionsVO } from '../services/aiAssistant';

export interface ModuleMeta {
  moduleId: string;
  pageTitle: string;
}

export type RequestFn = (path: string, params?: Record<string, any>, payload?: any) => Promise<any>;

export type FileRequestFn = (
  path: string,
  params: Record<string, any>,
  payload?: any
) => Promise<any>;

export interface AiCoreRuntime {
  /* ── HTTP ── */
  /** 应用侧 axios 封装，响应拦截器需保留 4001 → `isOidcRequired` 语义 */
  request: RequestFn;
  /** 文件上传封装（recommend 当前缺此能力，接入前需补） */
  fileRequest: FileRequestFn;
  /** 取 Authorization，流式请求手动拼 header 用 */
  getToken: () => string | undefined;
  /** 非流式接口的 axios baseURL，流式端点靠它拼绝对路径。默认 '/promeApi' */
  apiBase?: string;

  /* ── 路由 ── */
  /** qiankun 子应用 base，如 '/abtest' / '/widget' / '/smart' */
  appBase: string;
  /** 应用内跳转，路径**不含** appBase（各应用 history 已带 base） */
  navigate: (path: string) => void;
  /** 订阅路由变化，回调收完整 pathname（含 appBase）；返回取消订阅函数 */
  subscribeRoute: (cb: (pathname: string) => void) => () => void;

  /* ── 模块映射 ── */
  /** 路由 → moduleId 映射表，key 为含 appBase 的完整 pathname */
  moduleMap: Record<string, ModuleMeta>;
  /** 浮窗隐藏前缀（黑名单）。与 showPaths 二选一 */
  hidePrefixes?: string[];
  /** 浮窗仅在这些路径显示（白名单，widget 用）。配了就忽略 hidePrefixes */
  showPaths?: string[];
  /** 本地兜底气泡，后端 /chat/suggestions 失败时用 */
  localSuggestions?: Record<string, ModuleSuggestionsVO>;

  /* ── 业务钩子 ── */
  /** 当前选中游戏 code；ab-test 读 localStorage，widget/smart 读页面 store */
  getGameId?: () => string | undefined;
  /** 后端下发实体刷新信号时的应用侧处理（失效缓存 / 跳转 / 派事件） */
  onEntityRefresh?: (action: EntityAction) => void;
  /** 权限中心下发的权限项，用于判断是否展示浮窗 */
  getAuthKeys?: () => Promise<string[]>;
}

let runtime: AiCoreRuntime | null = null;

export function setAiRuntime(next: AiCoreRuntime) {
  runtime = next;
}

export function getAiRuntime(): AiCoreRuntime {
  if (!runtime) {
    throw new Error('[prome-core] 未初始化，请在应用入口调用 setAiRuntime()');
  }
  return runtime;
}

/** 读配置项，未初始化时不抛错（供渲染期可选读取） */
export function peekAiRuntime(): AiCoreRuntime | null {
  return runtime;
}

export const request: RequestFn = (path, params, payload) =>
  getAiRuntime().request(path, params, payload);

export const fileRequest: FileRequestFn = (path, params, payload) =>
  getAiRuntime().fileRequest(path, params, payload);

export const getToken = (): string | undefined => getAiRuntime().getToken();

export const getApiBase = (): string => getAiRuntime().apiBase ?? '/promeApi';

/** 拼接流式端点绝对路径 */
export const streamUrl = (path: string): string => `${getApiBase()}${path}`;
